import React, { useMemo } from 'react'
import OrdinalsRender from 'components/answers/FileEmbed/OrdinalsRender'
import type { IOrdinalDescriptor } from 'api/api'
import useStore from 'state/knovStore'

function withPreviewSize(url: string | undefined, size: 'small' | 'medium' | 'large' | 'original') {
    if (!url) return undefined
    try {
        const u = new URL(url, window.location.href)
        // Only mutate our stable Rails preview endpoint; leave external URLs untouched.
        if (/^\/api\/v1\/bsv\/ordinals\/[^\/]+\/preview_image$/i.test(u.pathname || '')) {
            u.searchParams.set('size', size)
            return u.toString()
        }
        return url
    } catch {
        return url
    }
}

export default function ResolvedOrdinalDescriptorRender({
    descriptor,
    className,
    allowInteraction,
    autoCapture3dPreview,
    forceLive,
    onRequestLive,
    htmlAlign = 'top-center',
    fillVideoContainer = false,
}: {
    descriptor: IOrdinalDescriptor
    className?: string
    allowInteraction: boolean
    autoCapture3dPreview?: boolean
    forceLive?: boolean
    onRequestLive?: () => void
    htmlAlign?: 'top-center' | 'center'
    fillVideoContainer?: boolean
}) {
    const debug = useStore(state => state.DEBUG)
    const url = descriptor?.content_url || null
    const contentType = descriptor?.content_type || undefined
    const basePreviewUrl = descriptor?.preview_url || undefined
    const previewUrl =
        allowInteraction &&
        String(contentType || '')
            .toLowerCase()
            .startsWith('image/')
            ? withPreviewSize(basePreviewUrl, 'original')
            : basePreviewUrl

    const outpoint = useMemo(() => {
        return descriptor?.origin_outpoint || descriptor?.outpoint
    }, [descriptor])

    if (!descriptor || descriptor.status !== 'resolved' || !url) {
        if (debug) {
            console.debug(
                '[ResolvedOrdinalDescriptorRender] not renderable',
                JSON.stringify({
                    status: descriptor?.status,
                    outpoint: descriptor?.outpoint,
                    origin_outpoint: descriptor?.origin_outpoint,
                    has_content_url: !!descriptor?.content_url,
                    content_type: descriptor?.content_type,
                }),
            )
        }
        return <div className={className}>Resolving ordinal…</div>
    }

    return (
        <OrdinalsRender
            outpoint={outpoint || ''}
            url={url}
            bsvOrdinalId={descriptor?.bsv_ordinal_id || undefined}
            contentType={contentType}
            filename={descriptor?.filename || undefined}
            previewUrl={previewUrl}
            className={className}
            allowInteraction={allowInteraction}
            useBabylonForGltf={true}
            autoCapture3dPreview={!!autoCapture3dPreview}
            onRequestLive={onRequestLive}
            forceLive={!!forceLive}
            htmlAlign={htmlAlign}
            fillVideoContainer={fillVideoContainer}
        />
    )
}
